import React, { useState } from "react";
import { Modal, Button, Form, Alert, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { validatePassword, getPasswordRequirements } from "../utils/passwordValidation";

interface Props {
  show: boolean;
  onHide: () => void;
}

const MustChangePasswordModal: React.FC<Props> = ({ show, onHide }) => {
  const { logout, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "danger";
    text: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate password complexity
    const errors = validatePassword(password, t);
    if (errors.length > 0) {
      setMessage({
        type: "danger",
        text: errors.join(". "),
      });
      return;
    }
    
    // Prevent setting the password back to the default temporary password
    const defaultPw = `${user?.username}-4568`;
    if (password === defaultPw) {
      setMessage({
        type: "danger",
        text: t("mustChange.passwordCannotBeDefault"),
      });
      return;
    }
    if (password !== confirm) {
      setMessage({ type: "danger", text: t("mustChange.passwordsDontMatch") });
      return;
    }

    try {
      setSubmitting(true);
      await axios.put("/api/profile", { password });
      setMessage({ type: "success", text: t("mustChange.updatedSuccess") });
      
      // Wait a moment to show success message
      setTimeout(async () => {
        setMessage(null);
        onHide();
        // Logout and redirect to login (password change invalidates session)
        await logout();
        navigate("/login", { replace: true });
      }, 2000);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || t("mustChange.updateError");
      setMessage({ type: "danger", text: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={() => {}} backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>{t("mustChange.title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && <Alert variant={message.type}>{message.text}</Alert>}
        <p>{t("mustChange.securityNote")}</p>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{t("mustChange.newPasswordLabel")}</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
              </Button>
            </InputGroup>
            <Form.Text className="text-muted">
              {getPasswordRequirements(t)}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("mustChange.confirmPasswordLabel")}</Form.Label>
            <InputGroup>
              <Form.Control
                type={showConfirm ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowConfirm(!showConfirm)}
                tabIndex={-1}
              >
                <i className={`bi ${showConfirm ? "bi-eye-slash" : "bi-eye"}`}></i>
              </Button>
            </InputGroup>
          </Form.Group>

          <Button type="submit" disabled={submitting} className="w-100">
            {t("mustChange.saveButton")}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default MustChangePasswordModal;
